<template>
  <div class="cws-container">
    <b-row>
      <b-col cols="12">
        <b-card title="Антишазам пакета" border-variant="primary">
          <b-card-text>
            Загрузите .siq — все аудио из папки <code>Audio</code> будут прогнаны через ffmpeg
            (<code>atempo</code> + <code>asetrate</code>), на выходе тот же пакет с обработанными файлами.
          </b-card-text>

          <b-form-group label="Пакет (.siq)" label-for="antishazam-file">
            <b-form-file
                id="antishazam-file"
                v-model="file"
                accept=".siq"
                placeholder="Выберите .siq..."
                browse-text="Обзор"
                :disabled="isProcessing"
            />
          </b-form-group>

          <b-row>
            <b-col md="2">
              <b-form-group label="Тон (tone)" description="Множитель частоты, 1.05 — выше на ~5%">
                <b-form-input v-model.number="form.tone" type="number" step="0.01" min="0.5" max="2" :disabled="isProcessing"/>
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Темп (temp)" description="atempo, 0.98 — медленнее на 2%">
                <b-form-input v-model.number="form.tempo" type="number" step="0.01" min="0.5" max="2" :disabled="isProcessing"/>
              </b-form-group>
            </b-col>
            <b-col md="8" class="d-flex align-items-center">
              <b-form-checkbox v-model="form.verify" :disabled="isProcessing">
                Проверить Шазамом до/после (2 запроса RapidAPI на файл)
              </b-form-checkbox>
            </b-col>
          </b-row>

          <b-button variant="primary" :disabled="!file || isProcessing" @click="runAntishazam">
            <b-spinner v-if="isProcessing" small class="mr-1"/>
            Обработать
          </b-button>

          <div v-if="isProcessing || statusText" class="mt-3">
            <b-progress :max="progressMax" height="1.2rem">
              <b-progress-bar :value="progressValue" :variant="progressVariant">
                {{ progressLabel }}
              </b-progress-bar>
            </b-progress>
            <small class="text-muted">{{ statusText }}</small>
          </div>

          <b-button v-if="downloadInfo" variant="success" class="mt-3" @click="download">
            <feather-icon icon="DownloadIcon" class="mr-50"/>
            Скачать {{ downloadInfo.filename }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-if="files.length">
      <b-col cols="12">
        <b-card no-body>
          <b-table :items="files" :fields="fields" small responsive class="mb-0">
            <template #cell(status)="{ item }">
              <b-badge v-if="item.ok" variant="light-success">обработан</b-badge>
              <b-badge v-else variant="light-danger" v-b-tooltip.hover :title="item.error">ошибка</b-badge>
            </template>
            <template #cell(before)="{ item }">
              <span v-if="item.verify">{{ item.verify.before.summary }}</span>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(after)="{ item }">
              <span v-if="item.verify" :class="afterClass(item.verify.after)">{{ item.verify.after.summary }}</span>
              <span v-else class="text-muted">—</span>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BFormInput, BFormCheckbox,
  BButton, BSpinner, BProgress, BProgressBar, BTable, BBadge, VBTooltip,
} from 'bootstrap-vue'
import { ref, reactive, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'

export default {
  components: {
    BRow, BCol, BCard, BCardText, BFormGroup, BFormFile, BFormInput, BFormCheckbox,
    BButton, BSpinner, BProgress, BProgressBar, BTable, BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const file = ref(null)
    const isProcessing = ref(false)
    const statusText = ref('')
    const downloadInfo = ref(null)
    const progressValue = ref(0)
    const progressMax = ref(1)
    const progressVariant = ref('primary')
    const files = ref([])

    const form = reactive({ tone: 1.05, tempo: 0.98, verify: false })

    const fields = [
      { key: 'file', label: 'Файл' },
      { key: 'status', label: 'Статус' },
      { key: 'before', label: 'Шазам до' },
      { key: 'after', label: 'Шазам после' },
    ]

    const progressLabel = computed(() => {
      if (progressMax.value <= 1 && !progressValue.value) return statusText.value ? '...' : ''
      return `${progressValue.value} / ${progressMax.value}`
    })

    // После обработки трек распознаваться не должен: распознан — плохо.
    const afterClass = result => {
      if (!result.ok) return 'text-warning'
      return result.summary.startsWith('✓') ? 'text-danger font-weight-bold' : 'text-success'
    }

    const onProgress = payload => {
      if (payload.event === 'start') {
        progressMax.value = payload.total || 1
        progressValue.value = 0
        statusText.value = payload.total ? `Аудиофайлов в пакете: ${payload.total}` : 'В пакете нет аудиофайлов'
      } else if (payload.event === 'file') {
        progressValue.value = payload.index + 1
        statusText.value = `Обработка: ${payload.file}`
        files.value.push(payload)
      } else if (payload.event === 'pack') {
        statusText.value = 'Сборка .siq...'
      } else if (payload.event === 'done') {
        progressValue.value = progressMax.value
        progressVariant.value = 'success'
        statusText.value = 'Готово'
      }
    }

    const runAntishazam = async () => {
      if (!file.value) return
      isProcessing.value = true
      downloadInfo.value = null
      files.value = []
      statusText.value = 'Загрузка пакета...'
      progressValue.value = 0
      progressMax.value = 1
      progressVariant.value = 'primary'

      try {
        downloadInfo.value = await store.dispatch('playlist/antishazamPack', {
          file: file.value,
          tone: form.tone,
          tempo: form.tempo,
          verify: form.verify,
          onProgress,
        })
      } catch (error) {
        progressVariant.value = 'danger'
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка антишазама', text: error.message, icon: 'AlertTriangleIcon', variant: 'danger',
          },
        })
      } finally {
        isProcessing.value = false
      }
    }

    const download = async () => {
      if (!downloadInfo.value) return
      try {
        await store.dispatch('playlist/downloadPackResult', {
          id: downloadInfo.value.download_id,
          filename: downloadInfo.value.filename,
        })
        // Ссылка одноразовая: бэкенд удаляет файл после отдачи.
        downloadInfo.value = null
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка скачивания', text: error.message, icon: 'AlertTriangleIcon', variant: 'danger',
          },
        })
      }
    }

    return {
      file, isProcessing, statusText, downloadInfo, files, fields,
      progressValue, progressMax, progressVariant, progressLabel,
      form, runAntishazam, download, afterClass,
    }
  },
}
</script>
